import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

import DefaultLayout from "/home/runner/work/uxp-photoshop/uxp-photoshop/node_modules/@adobe/gatsby-theme-aio/src/components/MDXFilter/index.js";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "photoshop-api"
    }}>{`Photoshop API`}</h1>
    <h2 {...{
      "id": "overview"
    }}>{`Overview`}</h2>
    <p>{`The following line allows you access to the Photoshop DOM via UXP.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`const app = require('photoshop').app;
`}</code></pre>
    <p>{`From here, you can open documents, modify them, run menu items, and more.`}</p>
    <h2 {...{
      "id": "synchronous-vs-asynchronous"
    }}>{`Synchronous vs Asynchronous`}</h2>
    <p>{`An important difference between ExtendScript (and CEP) and UXP in Photoshop is that all ExtendScript calls to Photoshop were synchronous. This means they blocked the Photoshop UI while they were executing. In UXP, a method call is `}<em parentName="p">{`asynchronous`}</em>{`, and does not block the UI thread.`}</p>
    <p>{`For a smooth transition between the ExtendScript DOM and the UXP DOM, all properties (get and set) in the API were designed to be `}<em parentName="p">{`synchronous`}</em>{` and do not need to be awaited. It is worth noting that they are, in the background, asynchronous in nature.`}</p>
    <h2 {...{
      "id": "working-with-photoshop-objects"
    }}>{`Working with Photoshop Objects`}</h2>
    <h3 {...{
      "id": "photoshop-application"
    }}>{`Photoshop Application`}</h3>
    <p>{`Through the `}<a parentName="p" {...{
        "href": "#overview"
      }}><inlineCode parentName="a">{`app`}</inlineCode></a>{` object, you can access the rest of Photoshop's objects and methods.`}</p>
    <p>{`The currently-active document is obtained like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`const doc = app.activeDocument;
`}</code></pre>
    <p>{`And you can get an array of all open documents like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`const allDocuments = app.documents;
`}</code></pre>
    <p>{`See more properties and methods in `}<inlineCode parentName="p">{`app`}</inlineCode>{` under `}<a parentName="p" {...{
        "href": "./classes/photoshop/"
      }}>{`Photoshop`}</a>{`.`}</p>
    <h3 {...{
      "id": "detour---executeasmodal"
    }}>{`Detour - ExecuteAsModal`}</h3>
    <p>{`A key concept to understand before diving straight into Photoshop UXP plugin development is what we have termed "execute as modal". Any and all commands that may `}<strong parentName="p">{`modify the document`}</strong>{`, or the `}<strong parentName="p">{`application state`}</strong>{`, must utilize executeAsModal.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`async function makeDefaultDocument(executionControl) {
  const app = require('photoshop').app;
  let myDoc = await app.createDocument({preset: "My Web Preset 1"});
}

await require('photoshop').core.executeAsModal(makeDefaultDocument);
`}</code></pre>
    <p>{`As you may notice, this restriction could encompass much of your plugins' functionality! There are many benefits for this model, however. A more detailed explanation is provided in the `}<a parentName="p" {...{
        "href": "./media/executeasmodal/"
      }}>{`Execute as Modal documentation`}</a>{`.`}</p>
    <h3 {...{
      "id": "document"
    }}>{`Document`}</h3>
    <p>{`Represents a single, open Photoshop document. From this object, you can access the document's layers, dimensions, resolution, etc. You can crop it, add/delete/duplicate layers, resize, rotate, and save it.`}</p>
    <p>{`Get the dimensions of the active document:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`const app = require('photoshop').app;
const myDoc = app.activeDocument;
const height = myDoc.height;
const width = myDoc.width;
const resolution = myDoc.resolution;
console.log(\`Doc size is \${width} x \${height}. Resolution is \${resolution}\`);
`}</code></pre>
    <p>{`Flatten all currently open documents:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`const app = require('photoshop').app;
const toFlatten = app.documents;
async function flattenThem(executionControl) {
  toFlatten.forEach((photoshopDoc) => {
    photoshopDoc.flatten();
  });
};

await require('photoshop').core.executeAsModal(flattenThem);
`}</code></pre>
    <p>{`Create a layer:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`const app = require('photoshop').app;
async function newColorDodgeLayer(executionControl) {
  await app.activeDocument.createLayer({ name: "myLayer", opacity: 80, mode: "colorDodge" });
};

await require('photoshop').core.executeAsModal(newColorDodgeLayer);
`}</code></pre>
    <p>{`See more properties and methods regarding `}<inlineCode parentName="p">{`Document`}</inlineCode>{` under `}<a parentName="p" {...{
        "href": "./classes/document/"
      }}>{`Document`}</a>{` and `}<a parentName="p" {...{
        "href": "./classes/documents"
      }}>{`Documents`}</a>{`.`}</p>
    <h3 {...{
      "id": "layer"
    }}>{`Layer`}</h3>
    <p>{`Represents a Layer, or a group of Layers. This object is tied to a particular `}<a parentName="p" {...{
        "href": "#Document"
      }}>{`Document`}</a>{`.`}</p>
    <p>{`Decrease a layer's opacity and bring it to the front:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`const app = require('photoshop').app;
const doc = app.activeDocument;
function bringActiveLayerToFront(executionControl) {
  const layer = doc.activeLayers[0];
  layer.opacity = layer.opacity - 10;
  layer.bringToFront();
};

await require('photoshop').core.executeAsModal(bringActiveLayerToFront);
`}</code></pre>
    <p>{`Scale down each layer whose name includes 'smaller'`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`const app = require('photoshop').app;
const doc = app.activeDocument;
const layers = doc.layers;
async function scaleLayers(executionControl) {
  for (layer of layers) {
    if (layer.name.includes('smaller')) {
      await layer.scale(80, 80);
    }
  }
};

await require('photoshop').core.executeAsModal(scaleLayers);
`}</code></pre>
    <p>{`Note that a layer's `}<inlineCode parentName="p">{`kind`}</inlineCode>{` property can be `}<inlineCode parentName="p">{`GROUP`}</inlineCode>{` for a Group layer (a layer or folder containing multiple layers). To access the layers in a Group layer, use the `}<inlineCode parentName="p">{`layers`}</inlineCode>{` property, and the `}<inlineCode parentName="p">{`parent`}</inlineCode>{` property to navigate the layer list tree.`}</p>
    <p>{`See more properties and methods for `}<inlineCode parentName="p">{`Layer`}</inlineCode>{` under `}<a parentName="p" {...{
        "href": "./classes/layer/"
      }}>{`Layer`}</a>{` and `}<a parentName="p" {...{
        "href": "./classes/layers/"
      }}>{`Layers`}</a>{`.`}</p>
    <h3 {...{
      "id": "actions-and-actionsets"
    }}>{`Actions and ActionSets`}</h3>
    <p>{`Many Photoshop users make heavy use of the `}<inlineCode parentName="p">{`Actions`}</inlineCode>{` panel. Actions are essentially macros that can be recorded and played back to script commands and tools that you use frequently. Actions are grouped into `}<inlineCode parentName="p">{`Action Sets`}</inlineCode>{`, similar to the way layers can be grouped into Group layers.`}</p>
    <p>{`The Actions object allows you to delete, duplicate, rename, and play actions. There is no current way to `}<em parentName="p">{`create`}</em>{` an action using UXP.`}</p>
    <p>{`Similarly to Actions, the ActionSet object allows you to delete, duplicate, rename, and play Action Sets. There is no current way to `}<em parentName="p">{`create`}</em>{` an Action Set.`}</p>
    <p>{`Note that Actions and Action Sets exist app-wide; they're not tied to a specific Document.`}</p>
    <p>{`Here's an example that finds a particular Action in the default Action Set, then plays it if it exists:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`const app = require('photoshop').app;
const allActionSets = app.actionTree;
const firstActionSet = allActionSets[0];
let actions = new Map(); // a JS Map allows easy "find by name" operations
firstActionSet.actions.forEach((action) => { actions.set(action.name, action)});
const myAction = actions.get("Wood Frame - 50 pixel");
if (myAction) { // user may have deleted this action
  async function playMyAction(executionControl) {
    await myAction.play();
  }
  await require('photoshop').core.executeAsModal(playMyAction);
}
`}</code></pre>
    <p>{`See more properties and methods for `}<inlineCode parentName="p">{`Action`}</inlineCode>{` under `}<a parentName="p" {...{
        "href": "./classes/action/"
      }}>{`Action`}</a>{` and `}<a parentName="p" {...{
        "href": "./classes/actionset/"
      }}>{`ActionSet`}</a></p>
    <h3 {...{
      "id": "batchplay"
    }}>{`batchPlay`}</h3>
    <p>{`Photoshop is complex software, with many internal classes and methods. Not all of these are yet exposed via UXP. New interfaces are in development and will be shipped along with each release of Photoshop. In the meantime, if there is something your plugin needs to do that is not exposed in the current DOM, you may be able to use `}<inlineCode parentName="p">{`batchPlay`}</inlineCode>{`.`}</p>
    <p>{`BatchPlay is for accessing Photoshop functionality that has not yet been exposed via APIs. BatchPlay is a way to send multiple actions into the Photoshop event queue and return their results.`}</p>
    <p>{`ExtendScript has `}<inlineCode parentName="p">{`executeAction`}</inlineCode>{`; this is analagous to UXP's `}<inlineCode parentName="p">{`batchPlay`}</inlineCode>{`. However, whereas `}<inlineCode parentName="p">{`executeAction`}</inlineCode>{` could only play one descriptor at a time, `}<inlineCode parentName="p">{`batchPlay`}</inlineCode>{` accepts an array of action descriptors. If you have multiple Photoshop operations that need to execute in series, using an array of action descriptors in a single `}<inlineCode parentName="p">{`batchPlay`}</inlineCode>{` call is probably what you want.`}</p>
    <p>{`Unlike ExtendScript where classes were provided to construct action descriptors, references and values, `}<inlineCode parentName="p">{`batchPlay`}</inlineCode>{` accepts plain JSON objects.`}</p>
    <p>{`The `}<a parentName="p" {...{
        "href": "/photoshop/uxp/ps_reference/media/batchplay/"
      }}>{`batchPlay documentation`}</a>{` contains details on how to construct JSON for `}<inlineCode parentName="p">{`batchPlay`}</inlineCode>{` usage.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      